// ruleEngine.js — the auto-tagging RULES ENGINE core.
//
// PURE, NO I/O. Two responsibilities, both unit-tested directly:
//   1. evaluateRule(caseFields, rule, ctx)  — recursive AND/OR evaluation in JS.
//   2. validateRule(rule)                   — server-side structural + allowlist guard.
//
// Rule model (recursive AND/OR groups), stored as JSON in tags.rules:
//   Group     = { enabled: boolean, match: 'all' | 'any', rules: [ Node ] }
//   Node      = Group | Leaf | Aggregate | FileCount
//   Leaf      = { field, op, value }
//                ops (string):  equals | notEquals | in | notIn
//                ops (numeric): greaterThan | greaterOrEqual | lessThan | lessOrEqual
//   Aggregate = { type:'aggregate', field, window:<int|'all'>, op:<count op>, value:<int> }
//                count ops: countEquals | countGreaterOrEqual | countGreaterThan |
//                           countLessThan | countLessOrEqual
//   FileCount = { type:'fileCount', category:<attachment category key>, op:<count op>, value:<int ≥ 0> }
//                counts THIS case's non-deleted attachments in that category slot;
//                the count is injected via ctx.fileCounts (never queried here).
//   OutstandingRequirement = { type:'outstandingRequirement', key:'any'|<category|fieldKey|reqKey>, minDays?:<int ≥ 0> }
//                matches when ctx.outstanding (outstanding.js — matrix uploads
//                minus waivers, blank matrix fields, per-case documents/sign-offs)
//                holds an item with that key ('any' = any item) that has been
//                outstanding for ≥ minDays whole days since its `since` clock
//                (ctx.now injected). A null ctx.outstanding fails closed.
//
// SECURITY — the field/operator ALLOWLISTS below are the SINGLE source of truth.
// Rules may only reference allowed case fields and allowed operators; anything
// else is rejected at validation. Leaf rules are evaluated in JS against the
// loaded case row. Aggregate counts come from an INJECTED provider (a bounded
// PARAMETERIZED count, or a precomputed in-memory index) whose SQL column is
// resolved through AGGREGATE_FIELDS — NEVER from raw rule input. NO SQL is ever
// built from rule input (no injection surface).
//
// TEMPORAL fields are DERIVED from injected context (`ctx.now`, the persisted
// case dates, the latest status change). They are NOT row columns — purity is
// preserved by passing `now` in via ctx, so tests stay deterministic and the
// engine never reads the wall clock.

import { MATRIX_FIELD_KEYS } from './requirementsMatrix.js';
import { outstandingAgeDays } from './outstanding.js';

// ── ALLOWLIST (single source of truth) ────────────────────────────────────────
// Adding a new allowed field is a single edit here (+ a mapping in fieldsFromCaseRow
// for taxonomy fields, in deriveTemporalFields for temporal, or in evaluateRule's
// ctx-derived bags for context fields). The frontend mirrors RULE_FIELDS /
// RULE_OPERATORS for its pickers. Which fields a NOTIFICATION rule may use is
// gated per rule kind in notificationSchema.js; this engine only owns structure.

/** Allowed STRING/taxonomy rule fields read from the CASE ROW (string ops). */
export const STRING_FIELDS = Object.freeze([
  'treatment',
  'treatmentSubtype',
  'severity',
  'status',
  'eventType',
  'incidentCategory',
  'clinic',
  'region',
  'country',
]);

/**
 * Ctx-derived STRING fields (string ops) — values come from the injected
 * evaluation ctx, NOT from the case row:
 *   ops               — the case's country group ('IOI' | 'GB'), from ctx.ops.
 *   previousSeverity / previousStatus — the pre-change values, from ctx.previous;
 *                       only populated on change-trigger dispatches, otherwise
 *                       null (fail-closed for positive ops).
 *   requirementKey / requirementLabel — the requirement being added/signed off,
 *                       from ctx.requirement; only populated on requirement-
 *                       trigger dispatches, otherwise null (fail-closed).
 */
export const CONTEXT_STRING_FIELDS = Object.freeze([
  'ops',
  'previousSeverity',
  'previousStatus',
  'requirementKey',
  'requirementLabel',
]);

/**
 * Derived NUMERIC (non-temporal) fields (numeric ops):
 *   severityLevel         — severity 'Code N' → N, anything else → null.
 *   previousSeverityLevel — same derivation over ctx.previous.severity.
 */
export const NUMERIC_FIELDS = Object.freeze([
  'severityLevel',
  'previousSeverityLevel',
]);

/**
 * previous-state fields — a subset of the above that is only meaningful on a
 * change-trigger dispatch (status_changed / severity_changed). notificationSchema
 * gates them per rule kind; here they simply fail closed when ctx.previous is null.
 */
export const PREVIOUS_FIELDS = Object.freeze([
  'previousSeverity',
  'previousStatus',
  'previousSeverityLevel',
]);

/**
 * requirement-scoped fields — only meaningful on a requirement-trigger dispatch
 * (requirement_added / requirement_signed_off). notificationSchema gates them
 * per rule kind; here they simply fail closed when ctx.requirement is null.
 */
export const REQUIREMENT_FIELDS = Object.freeze([
  'requirementKey',
  'requirementLabel',
]);

/** Allowed TEMPORAL rule fields (derived day-counts; compared with numeric ops). */
export const TEMPORAL_FIELDS = Object.freeze([
  'daysFromIncident',
  'daysFromReported',
  'daysInCurrentStatus',
  'daysUntilLegalThreshold',
  'daysSinceLastActivity',
]);

/** Every allowed leaf field. */
export const RULE_FIELDS = Object.freeze([
  ...STRING_FIELDS,
  ...CONTEXT_STRING_FIELDS,
  ...NUMERIC_FIELDS,
  ...TEMPORAL_FIELDS,
]);

/**
 * TAG_RULE_FIELDS — the leaf fields a TAG rule may reference. The tag
 * evaluation ctx (ruleContext.js) supplies now/statusSince/legalThresholdMonths
 * + an aggregateCount provider but NEVER ctx.ops / ctx.previous /
 * ctx.requirement / ctx.lastActivityMs / ctx.fileCounts — those are notification-dispatch ctx
 * (notificationContext.js). A tag rule referencing them would fail closed
 * forever, and inside a match:'all' group the nightly reconcile would then
 * STRIP the tag from every case that had it. severityLevel stays allowed: it
 * derives in-engine from the row's severity, so it works in every tag path.
 */
export const TAG_RULE_FIELDS = Object.freeze([
  ...STRING_FIELDS,
  'severityLevel',
  'daysFromIncident',
  'daysFromReported',
  'daysInCurrentStatus',
  'daysUntilLegalThreshold',
]);

/**
 * field → kind map: 'string' (taxonomy/ctx string) or 'numeric' (derived number
 * or temporal day-count). Temporal-vs-plain-numeric is distinguished by
 * membership in TEMPORAL_FIELDS, not by a third kind.
 */
export const FIELD_KINDS = Object.freeze(
  Object.fromEntries([
    ...STRING_FIELDS.map(f => [f, 'string']),
    ...CONTEXT_STRING_FIELDS.map(f => [f, 'string']),
    ...NUMERIC_FIELDS.map(f => [f, 'numeric']),
    ...TEMPORAL_FIELDS.map(f => [f, 'numeric']),
  ])
);

/**
 * ATTACHMENT_CATEGORY_KEYS — the canonical attachment category vocabulary a
 * fileCount node may target. This is the SERVER-side source of truth (the
 * wizard slot categories); frontend/src/lib/eventVocabularies.js
 * ATTACHMENT_CATEGORIES mirrors it with labels and must stay in sync.
 */
export const ATTACHMENT_CATEGORY_KEYS = Object.freeze([
  'photo',
  'training_certificate',
  'consent_form',
  'treating_therapist_statement',
  'pre_photo',
  'post_photo',
  'dosage_chart',
  'laser_log_files',
  'injector_consultancy_agreement',
  'injector_insurance_policy',
  'other',
]);

/**
 * OUTSTANDING_REQUIREMENT_KEYS — the picker vocabulary for an
 * outstandingRequirement node: 'any', every canonical upload category the
 * matrix can require and every matrix-required type_specific field key.
 * Excluded: 'other' (never required) and 'photo' (the universal photo baseline
 * is deliberately NOT mirrored by requirementsMatrix, so outstanding.js never
 * derives it — offering it would build a rule that can never fire). Per-case
 * (admin-added) requirements that resolve to a canonical category
 * (requirements.js canonicalCategoryForRequirement — e.g. a per-case
 * "Training certificate") are keyed by THAT category in outstanding.js, so the
 * picker's category keys catch them too. The rest keep their config-driven
 * requirementTypes key, so validation ALSO accepts any slug-shaped key (see
 * OUTSTANDING_KEY_RE) — reachable by that key or via 'any'.
 */
const NEVER_OUTSTANDING_CATEGORIES = new Set(['other', 'photo']);
export const OUTSTANDING_REQUIREMENT_KEYS = Object.freeze([
  'any',
  ...ATTACHMENT_CATEGORY_KEYS.filter(k => !NEVER_OUTSTANDING_CATEGORIES.has(k)),
  ...MATRIX_FIELD_KEYS,
]);
const OUTSTANDING_KEY_RE = /^[a-z0-9][a-z0-9_:.-]{0,59}$/i;

/** String operators — only valid on string/taxonomy fields. */
export const STRING_OPERATORS = Object.freeze(['equals', 'notEquals', 'in', 'notIn']);

/** Numeric operators — only valid on numeric/temporal fields. */
export const NUMERIC_OPERATORS = Object.freeze(['greaterThan', 'greaterOrEqual', 'lessThan', 'lessOrEqual']);

/** Every allowed leaf operator. */
export const RULE_OPERATORS = Object.freeze([...STRING_OPERATORS, ...NUMERIC_OPERATORS]);

/** Count operators for an aggregate node. */
export const COUNT_OPERATORS = Object.freeze([
  'countEquals',
  'countGreaterOrEqual',
  'countGreaterThan',
  'countLessThan',
  'countLessOrEqual',
]);

/**
 * AGGREGATE_FIELDS — rule key → REAL `cases` column. The aggregate node's `field`
 * is resolved through THIS map; the column name is NEVER taken from raw rule input.
 * Adding a new aggregable people-field is a single edit here (+ a parameterised
 * count using the mapped column).
 */
export const AGGREGATE_FIELDS = Object.freeze({
  staffMemberConcerned:     'staff_member_concerned',
  clinicManagerResponsible: 'clinic_manager_responsible',
  medicalTeamMember:        'medical_team_member',
});

/** Default legal-threshold window (months) when admin config is absent. */
export const LEGAL_THRESHOLD_MONTHS_DEFAULT = 6;

const STRING_KIND_SET    = new Set([...STRING_FIELDS, ...CONTEXT_STRING_FIELDS]);
const TAG_FIELD_SET      = new Set(TAG_RULE_FIELDS);
const NUMERIC_KIND_SET   = new Set([...NUMERIC_FIELDS, ...TEMPORAL_FIELDS]);
const FIELD_SET          = new Set(RULE_FIELDS);
const STRING_OP_SET      = new Set(STRING_OPERATORS);
const NUMERIC_OP_SET     = new Set(NUMERIC_OPERATORS);
const COUNT_OP_SET       = new Set(COUNT_OPERATORS);
const AGG_FIELD_SET      = new Set(Object.keys(AGGREGATE_FIELDS));
const ATTACHMENT_CATEGORY_SET = new Set(ATTACHMENT_CATEGORY_KEYS);

// Hard caps so a hand-crafted rule can't blow up evaluation/validation.
const MAX_DEPTH         = 10;
const MAX_CONDITIONS    = 200;     // total leaf/aggregate conditions across the tree
const MAX_IN_VALUES     = 200;     // values in a single in/notIn list
const MAX_NUMERIC_VALUE = 100000;  // bound for numeric/temporal comparison values
const MAX_COUNT_VALUE   = 100000;  // bound for an aggregate count target
const MAX_WINDOW_DAYS    = 36500;  // ~100y — bound for an aggregate trailing window

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ── Case row → normalised rule field bag ──────────────────────────────────────
// Maps a raw `cases` row (snake_case columns) to the allowlisted STRING rule field
// names. The evaluator is generic; this is the only place that knows the column
// mapping. `department` is the treatment column (department == treatment_type).
export function fieldsFromCaseRow(row = {}) {
  return {
    treatment:        normVal(row.department),
    treatmentSubtype: normVal(row.treatment_subtype),
    severity:         normVal(row.severity),
    status:           normVal(row.status),
    eventType:        normVal(row.event_type),
    incidentCategory: normVal(row.incident_category),
    clinic:           normVal(row.clinic),
    region:           normVal(row.region),
    country:          normVal(row.country),
  };
}

function normVal(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function eqValue(a, b) {
  if (a == null || b == null) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/**
 * severityLevelFromValue('Code N') → N (number). Anything else — 'Not AE',
 * free text, null — → null (fail-closed for numeric comparisons).
 */
export function severityLevelFromValue(severity) {
  if (severity == null) return null;
  const m = /^code\s*(\d+)$/i.exec(String(severity).trim());
  return m ? Number(m[1]) : null;
}

// ── TEMPORAL derivation (pure; from injected ctx) ──────────────────────────────

/** Coerce a value to a Date or null (never throws). */
function toDate(v) {
  if (v == null) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole days between two instants (a − b), rounded down. null when either is null. */
function diffDays(a, b) {
  if (a == null || b == null) return null;
  return Math.floor((a.getTime() - b.getTime()) / MS_PER_DAY);
}

/** Add `months` calendar months to a Date (clamps day-of-month). */
function addMonths(date, months) {
  const d = new Date(date.getTime());
  const targetMonth = d.getUTCMonth() + months;
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(targetMonth);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return d;
}

/**
 * deriveTemporalFields(caseRow, ctx) — compute the derived day-count fields
 * for ONE case. Each is `null` (fail-closed → condition won't match) when its
 * source date is missing.
 *
 * @param {object} caseRow  raw `cases` row (date_of_incident, created_at, …)
 * @param {object} ctx      { now, legalThresholdMonths, statusSince, lastActivityMs }
 *   - now:      Date|string instant the rule is evaluated AS-OF (injected).
 *   - statusSince: Date|string of the latest `status_changed` event_created_at;
 *                  falls back to the case created_at when never changed.
 *   - legalThresholdMonths: number of months (admin config; default 6).
 *   - lastActivityMs: epoch ms of the case's latest activity (case_events
 *                  MAX(created_at) excluding notification_sent rows) or null.
 */
export function deriveTemporalFields(caseRow = {}, ctx = {}) {
  const now = toDate(ctx.now);
  const incident = toDate(caseRow.date_of_incident);
  const reported = toDate(caseRow.created_at);
  const statusSince = toDate(ctx.statusSince) ?? reported;   // fallback: created_at
  const lastActivity = typeof ctx.lastActivityMs === 'number' && Number.isFinite(ctx.lastActivityMs)
    ? new Date(ctx.lastActivityMs)
    : null;
  const months = Number.isFinite(ctx.legalThresholdMonths)
    ? ctx.legalThresholdMonths
    : LEGAL_THRESHOLD_MONTHS_DEFAULT;

  return {
    daysFromIncident:        diffDays(now, incident),
    daysFromReported:        diffDays(now, reported),
    daysInCurrentStatus:     diffDays(now, statusSince),
    daysUntilLegalThreshold: incident && now
      ? diffDays(addMonths(incident, months), now)
      : null,
    daysSinceLastActivity:   diffDays(now, lastActivity),
  };
}

// ── EVALUATION (pure) ─────────────────────────────────────────────────────────

function evalNumeric(actual, op, value) {
  // Fail-closed: a missing temporal value (null) never matches.
  if (typeof actual !== 'number' || !Number.isFinite(actual)) return false;
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  switch (op) {
    case 'greaterThan':    return actual > value;
    case 'greaterOrEqual': return actual >= value;
    case 'lessThan':       return actual < value;
    case 'lessOrEqual':    return actual <= value;
    default:               return false;
  }
}

/**
 * Evaluate one LEAF condition against the normalised string-field bag + the
 * derived numeric bag (temporal day-counts + severity levels).
 * Unknown field/op (should never happen post-validation) → false (fail closed).
 */
function evalCondition(fields, numbers, cond) {
  if (!cond || typeof cond !== 'object') return false;
  if (!FIELD_SET.has(cond.field)) return false;

  if (NUMERIC_KIND_SET.has(cond.field)) {
    if (!NUMERIC_OP_SET.has(cond.op)) return false;   // numeric ops only
    return evalNumeric(numbers[cond.field] ?? null, cond.op, cond.value);
  }

  // String/taxonomy/ctx field.
  if (!STRING_OP_SET.has(cond.op)) return false;       // string ops only
  const actual = fields[cond.field] ?? null;
  switch (cond.op) {
    case 'equals':    return eqValue(actual, cond.value);
    case 'notEquals': return !eqValue(actual, cond.value);
    case 'in':        return Array.isArray(cond.value) && cond.value.some(v => eqValue(actual, v));
    case 'notIn':     return Array.isArray(cond.value) && !cond.value.some(v => eqValue(actual, v));
    default:          return false;
  }
}

function isAggregate(node) {
  return node && typeof node === 'object' && node.type === 'aggregate';
}

function isFileCount(node) {
  return node && typeof node === 'object' && node.type === 'fileCount';
}

/** Shared count comparator for aggregate + fileCount nodes. */
function compareCount(count, op, value) {
  switch (op) {
    case 'countEquals':         return count === value;
    case 'countGreaterOrEqual': return count >= value;
    case 'countGreaterThan':    return count > value;
    case 'countLessThan':       return count < value;
    case 'countLessOrEqual':    return count <= value;
    default:                    return false;
  }
}

function evalAggregate(node, ctx) {
  if (!AGG_FIELD_SET.has(node.field)) return false;
  if (!COUNT_OP_SET.has(node.op)) return false;
  if (typeof node.value !== 'number' || !Number.isFinite(node.value)) return false;
  const provider = ctx && typeof ctx.aggregateCount === 'function' ? ctx.aggregateCount : null;
  if (!provider) return false;   // no count provider injected → fail closed

  // Provider resolves the count for (rule key, this case's value, window). The
  // column is mapped from AGGREGATE_FIELDS inside the provider — never here.
  const count = provider(node.field, node.window);
  if (typeof count !== 'number' || !Number.isFinite(count)) return false;

  return compareCount(count, node.op, node.value);
}

/**
 * fileCount node — compares THIS case's attachment count for one category slot.
 * The count is read from ctx.fileCounts[category] (built by notificationContext
 * from non-deleted attachments rows). Missing map or missing key → null → fail
 * closed, matching every other absent-source semantic in this engine.
 */
function evalFileCount(node, ctx) {
  if (!ATTACHMENT_CATEGORY_SET.has(node.category)) return false;
  if (!COUNT_OP_SET.has(node.op)) return false;
  if (typeof node.value !== 'number' || !Number.isFinite(node.value)) return false;
  const counts = ctx && ctx.fileCounts && typeof ctx.fileCounts === 'object' ? ctx.fileCounts : null;
  if (!counts) return false;                            // no counts injected → fail closed
  const count = counts[node.category];
  if (typeof count !== 'number' || !Number.isFinite(count)) return false;   // missing key → fail closed

  return compareCount(count, node.op, node.value);
}

function isOutstandingRequirement(node) {
  return node && typeof node === 'object' && node.type === 'outstandingRequirement';
}

/**
 * outstandingRequirement node — does THIS case still have an outstanding item
 * with the node's key (or any item, key 'any') that has been outstanding for at
 * least `minDays` whole days? The list is injected as ctx.outstanding (built by
 * notificationContext via outstanding.js) and the clock is ctx.now. A missing /
 * non-array list fails closed, matching every other absent-source semantic.
 * minDays 0 (or absent) = "is outstanding at all" (no clock needed).
 */
function evalOutstandingRequirement(node, ctx) {
  if (!isValidOutstandingKey(node.key)) return false;
  const minDays = node.minDays == null ? 0 : node.minDays;
  if (!Number.isInteger(minDays) || minDays < 0) return false;
  const list = ctx && Array.isArray(ctx.outstanding) ? ctx.outstanding : null;
  if (!list) return false;                               // unknown state → fail closed
  const wanted = String(node.key).trim().toLowerCase();
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    if (wanted !== 'any' && String(item.key ?? '').trim().toLowerCase() !== wanted) continue;
    if (minDays === 0) return true;
    const age = outstandingAgeDays(item, ctx.now);
    if (typeof age === 'number' && age >= minDays) return true;
  }
  return false;
}

function isValidOutstandingKey(key) {
  if (typeof key !== 'string') return false;
  const k = key.trim();
  if (!k) return false;
  return k === 'any' || OUTSTANDING_KEY_RE.test(k);
}

function isGroup(node) {
  return node && typeof node === 'object'
    && !isAggregate(node) && !isFileCount(node) && !isOutstandingRequirement(node)
    && Array.isArray(node.rules);
}

function evalGroup(fields, numbers, ctx, group, depth) {
  if (depth > MAX_DEPTH) return false;
  const children = group.rules;
  // An empty group never matches (avoids "vacuous truth" auto-tagging everything).
  if (!Array.isArray(children) || children.length === 0) return false;

  const any = group.match === 'any';
  const results = children.map(node => {
    if (isGroup(node))     return evalGroup(fields, numbers, ctx, node, depth + 1);
    if (isAggregate(node)) return evalAggregate(node, ctx);
    if (isFileCount(node)) return evalFileCount(node, ctx);
    if (isOutstandingRequirement(node)) return evalOutstandingRequirement(node, ctx);
    return evalCondition(fields, numbers, node);
  });
  return any ? results.some(Boolean) : results.every(Boolean);
}

/**
 * evaluateRule(caseFields, rule, ctx) — does this rule match this case?
 *
 * @param {object} caseFields  EITHER a raw `cases` row (snake_case) OR an already
 *                             normalised field bag. Raw rows are detected by the
 *                             presence of a known column and mapped via fieldsFromCaseRow.
 * @param {object} rule        a top-level Group: { enabled, match, rules: [...] }
 * @param {object} [ctx]       evaluation context (PURE, injected):
 *   - now, statusSince, legalThresholdMonths, lastActivityMs → temporal derivation.
 *   - ops ('IOI'|'GB'|null) → the `ops` string field.
 *   - previous ({ severity, status }|null) → the previous* fields; null (the
 *     non-change-trigger case) fails them closed.
 *   - requirement ({ key, label }|null) → the requirement* fields; null (the
 *     non-requirement-trigger case) fails them closed.
 *   - fileCounts ({ <category>: int }) → fileCount nodes.
 *   - outstanding ([{ key, label, kind, since }]|null) → outstandingRequirement
 *     nodes (aged against ctx.now); null fails them closed.
 *   - aggregateCount(field, window) → resolves an aggregate node's corpus count.
 *   Omitting ctx is safe: temporal/aggregate/fileCount/ctx-derived nodes simply
 *   fail closed (no match, no throw); static string rules behave exactly as before.
 * @returns {boolean}          true when the case matches and the rule is enabled.
 *
 * Pure: no I/O, no wall-clock read, no throw on a malformed rule (returns false).
 */
export function evaluateRule(caseFields, rule, ctx = {}) {
  if (!isGroup(rule)) return false;
  if (rule.enabled === false) return false;   // disabled rule never matches

  const looksRaw = caseFields && (
    'department' in caseFields || 'event_type' in caseFields ||
    'treatment_subtype' in caseFields || 'incident_category' in caseFields ||
    'date_of_incident' in caseFields || 'created_at' in caseFields
  );
  const rowFields = looksRaw ? fieldsFromCaseRow(caseFields) : (caseFields || {});
  const previous  = ctx.previous && typeof ctx.previous === 'object' ? ctx.previous : null;
  const requirement = ctx.requirement && typeof ctx.requirement === 'object' ? ctx.requirement : null;

  // Ctx-derived string fields ride alongside the row fields; they are ALWAYS
  // taken from ctx (never the row), so an absent ctx key = null = fail closed.
  const fields = {
    ...rowFields,
    ops:              normVal(ctx.ops),
    previousSeverity: normVal(previous ? previous.severity : null),
    previousStatus:   normVal(previous ? previous.status : null),
    requirementKey:   normVal(requirement ? requirement.key : null),
    requirementLabel: normVal(requirement ? requirement.label : null),
  };
  // Numeric bag = temporal day-counts (raw rows only, as before) + the derived
  // severity levels ('Code N' → N, anything else → null).
  const numbers = {
    ...(looksRaw ? deriveTemporalFields(caseFields, ctx) : {}),
    severityLevel:         severityLevelFromValue(fields.severity),
    previousSeverityLevel: severityLevelFromValue(previous ? previous.severity : null),
  };

  return evalGroup(fields, numbers, ctx, rule, 0);
}

// ── VALIDATION (server-side, recursive) ───────────────────────────────────────

/**
 * validateRule(rule) — recursively validate a rule's STRUCTURE and enforce the
 * field/operator ALLOWLIST + the field→kind pairing + aggregate bounds. Returns
 * { ok, errors }. Called server-side before a rule is ever persisted; the engine
 * never trusts unvalidated input.
 *
 * A valid rule is a top-level Group. null/undefined is treated as "no rule".
 */
export function validateRule(rule) {
  const errors = [];
  if (rule == null) return { ok: true, errors };   // no rule = valid, matches nothing

  const counter = { conditions: 0 };
  validateGroup(rule, 0, 'rule', errors, counter, true);

  if (counter.conditions > MAX_CONDITIONS) {
    errors.push(`Rule has too many conditions (max ${MAX_CONDITIONS}).`);
  }
  return { ok: errors.length === 0, errors };
}

// Walk the tree for the TAG-side field gate; returns the first error string or
// null. Mirrors notificationSchema's conditionFieldError: structure/shape/bounds
// are validateRule's job — this only checks WHICH leaves a TAG rule may use.
// Aggregate nodes are tag-only and pass; fileCount / outstandingRequirement
// nodes are notification-only (their inputs come from the notification ctx)
// and are rejected.
function tagRuleFieldError(node, depth = 0) {
  if (node == null || typeof node !== 'object') return null;
  if (depth > MAX_DEPTH) return null;   // depth errors are validateRule's job
  if (isAggregate(node)) return null;
  if (isFileCount(node)) return 'fileCount conditions are not allowed in tag rules.';
  if (isOutstandingRequirement(node)) return 'outstandingRequirement conditions are not allowed in tag rules.';
  if (Array.isArray(node.rules)) {
    for (const child of node.rules) {
      const e = tagRuleFieldError(child, depth + 1);
      if (e) return e;
    }
    return null;
  }
  if (node.field != null && !TAG_FIELD_SET.has(node.field)) {
    return `field "${node.field}" is not allowed in tag rules.`;
  }
  return null;
}

/**
 * validateTagRule(rule) — validateRule PLUS the tag-side field gate. Use this
 * (never bare validateRule) wherever a TAG rule is accepted from a client
 * (putTagRule, postTagRulesPreview), so a notification-only field can never be
 * persisted into tags.rules and fail closed forever.
 */
export function validateTagRule(rule) {
  const { ok, errors } = validateRule(rule);
  const fe = tagRuleFieldError(rule);
  if (fe) return { ok: false, errors: [...errors, fe] };
  return { ok, errors };
}

function validateGroup(node, depth, path, errors, counter, isTop) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    errors.push(`${path}: expected a group object.`);
    return;
  }
  if (depth > MAX_DEPTH) {
    errors.push(`${path}: rule nesting too deep (max ${MAX_DEPTH}).`);
    return;
  }
  if (node.match !== 'all' && node.match !== 'any') {
    errors.push(`${path}: "match" must be "all" or "any".`);
  }
  if (isTop && node.enabled != null && typeof node.enabled !== 'boolean') {
    errors.push(`${path}: "enabled" must be a boolean.`);
  }
  if (!Array.isArray(node.rules)) {
    errors.push(`${path}: "rules" must be an array.`);
    return;
  }
  // Reject any unexpected keys to keep the stored shape tight.
  const allowedKeys = isTop ? ['enabled', 'match', 'rules'] : ['match', 'rules'];
  for (const k of Object.keys(node)) {
    if (!allowedKeys.includes(k)) errors.push(`${path}: unexpected key "${k}".`);
  }

  node.rules.forEach((child, i) => {
    const childPath = `${path}.rules[${i}]`;
    if (isAggregate(child)) {
      validateAggregate(child, childPath, errors, counter);
    } else if (isFileCount(child)) {
      validateFileCount(child, childPath, errors, counter);
    } else if (isOutstandingRequirement(child)) {
      validateOutstandingRequirement(child, childPath, errors, counter);
    } else if (isGroup(child)) {
      validateGroup(child, depth + 1, childPath, errors, counter, false);
    } else {
      validateCondition(child, childPath, errors, counter);
    }
  });
}

function validateCondition(cond, path, errors, counter) {
  counter.conditions++;
  if (!cond || typeof cond !== 'object' || Array.isArray(cond)) {
    errors.push(`${path}: expected a condition object.`);
    return;
  }
  for (const k of Object.keys(cond)) {
    if (!['field', 'op', 'value'].includes(k)) errors.push(`${path}: unexpected key "${k}".`);
  }
  if (!FIELD_SET.has(cond.field)) {
    errors.push(`${path}: field "${cond.field}" is not allowed.`);
    return;
  }
  if (!STRING_OP_SET.has(cond.op) && !NUMERIC_OP_SET.has(cond.op)) {
    errors.push(`${path}: operator "${cond.op}" is not allowed.`);
    return;   // can't sensibly check value shape without a known op
  }

  const kind = FIELD_KINDS[cond.field];

  // ── field→kind / op pairing: numeric ops only on temporal/numeric fields,
  //    string ops only on taxonomy/string fields. Reject any mismatch.
  if (kind === 'numeric') {
    if (!NUMERIC_OP_SET.has(cond.op)) {
      errors.push(`${path}: numeric field "${cond.field}" requires a numeric operator (greaterThan/greaterOrEqual/lessThan/lessOrEqual).`);
      return;
    }
    if (typeof cond.value !== 'number' || !Number.isFinite(cond.value)) {
      errors.push(`${path}: "${cond.op}" on "${cond.field}" requires a numeric value.`);
    } else if (Math.abs(cond.value) > MAX_NUMERIC_VALUE) {
      errors.push(`${path}: numeric value out of bounds (max ±${MAX_NUMERIC_VALUE}).`);
    }
    return;
  }

  // kind === 'string'
  if (!STRING_OP_SET.has(cond.op)) {
    errors.push(`${path}: string field "${cond.field}" requires a string operator (equals/notEquals/in/notIn).`);
    return;
  }
  if (cond.op === 'in' || cond.op === 'notIn') {
    if (!Array.isArray(cond.value)) {
      errors.push(`${path}: "${cond.op}" requires an array value.`);
    } else if (cond.value.length === 0) {
      errors.push(`${path}: "${cond.op}" value list must not be empty.`);
    } else if (cond.value.length > MAX_IN_VALUES) {
      errors.push(`${path}: "${cond.op}" value list too long (max ${MAX_IN_VALUES}).`);
    } else if (!cond.value.every(isScalarValue)) {
      errors.push(`${path}: "${cond.op}" values must be strings/numbers.`);
    }
  } else {
    // equals / notEquals — a single scalar value.
    if (!isScalarValue(cond.value)) {
      errors.push(`${path}: "${cond.op}" requires a string/number value.`);
    }
  }
}

function validateAggregate(node, path, errors, counter) {
  counter.conditions++;
  for (const k of Object.keys(node)) {
    if (!['type', 'field', 'window', 'op', 'value'].includes(k)) {
      errors.push(`${path}: unexpected key "${k}".`);
    }
  }
  if (!AGG_FIELD_SET.has(node.field)) {
    errors.push(`${path}: aggregate field "${node.field}" is not allowed.`);
  }
  if (!COUNT_OP_SET.has(node.op)) {
    errors.push(`${path}: aggregate operator "${node.op}" is not allowed.`);
  }
  // value: bounded non-negative integer.
  if (typeof node.value !== 'number' || !Number.isInteger(node.value) || node.value < 0) {
    errors.push(`${path}: aggregate "value" must be a non-negative integer.`);
  } else if (node.value > MAX_COUNT_VALUE) {
    errors.push(`${path}: aggregate "value" out of bounds (max ${MAX_COUNT_VALUE}).`);
  }
  // window: 'all' OR a bounded positive integer of days.
  if (node.window !== 'all') {
    if (typeof node.window !== 'number' || !Number.isInteger(node.window) || node.window <= 0) {
      errors.push(`${path}: aggregate "window" must be 'all' or a positive integer of days.`);
    } else if (node.window > MAX_WINDOW_DAYS) {
      errors.push(`${path}: aggregate "window" out of bounds (max ${MAX_WINDOW_DAYS} days).`);
    }
  }
}

function validateFileCount(node, path, errors, counter) {
  counter.conditions++;
  for (const k of Object.keys(node)) {
    if (!['type', 'category', 'op', 'value'].includes(k)) {
      errors.push(`${path}: unexpected key "${k}".`);
    }
  }
  if (!ATTACHMENT_CATEGORY_SET.has(node.category)) {
    errors.push(`${path}: fileCount category "${node.category}" is not allowed.`);
  }
  if (!COUNT_OP_SET.has(node.op)) {
    errors.push(`${path}: fileCount operator "${node.op}" is not allowed.`);
  }
  // value: bounded non-negative integer.
  if (typeof node.value !== 'number' || !Number.isInteger(node.value) || node.value < 0) {
    errors.push(`${path}: fileCount "value" must be a non-negative integer.`);
  } else if (node.value > MAX_COUNT_VALUE) {
    errors.push(`${path}: fileCount "value" out of bounds (max ${MAX_COUNT_VALUE}).`);
  }
}

function validateOutstandingRequirement(node, path, errors, counter) {
  counter.conditions++;
  for (const k of Object.keys(node)) {
    if (!['type', 'key', 'minDays'].includes(k)) {
      errors.push(`${path}: unexpected key "${k}".`);
    }
  }
  if (!isValidOutstandingKey(node.key)) {
    errors.push(`${path}: outstandingRequirement key "${node.key}" is not allowed (use 'any', an upload category, a required field key or a requirement type key).`);
  }
  // minDays: OPTIONAL bounded non-negative integer of whole days.
  if (node.minDays != null) {
    if (typeof node.minDays !== 'number' || !Number.isInteger(node.minDays) || node.minDays < 0) {
      errors.push(`${path}: outstandingRequirement "minDays" must be a non-negative integer.`);
    } else if (node.minDays > MAX_WINDOW_DAYS) {
      errors.push(`${path}: outstandingRequirement "minDays" out of bounds (max ${MAX_WINDOW_DAYS} days).`);
    }
  }
}

function isScalarValue(v) {
  return (typeof v === 'string' && v.length <= 200) || typeof v === 'number';
}
